import { useCallback, useEffect, useState } from 'react';
import { getMealPlanDraftById } from '@/lib/api/mealPlans';
import { FinalizedMealPlanData } from '@/types/meal-plan';

type MealPlanFetcher = (
  planId: string
) => Promise<{ success: boolean; data?: FinalizedMealPlanData | null; message?: string }>;

type RefreshOptions = {
  suppressLoading?: boolean;
};

export const useMealPlanData = (
  planId?: string,
  fetcher: MealPlanFetcher = getMealPlanDraftById
) => {
  const [data, setData] = useState<FinalizedMealPlanData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(
    async (options?: RefreshOptions) => {
      if (!planId) {
        setData(null);
        return null;
      }

      const suppressLoading = options?.suppressLoading ?? false;
      if (!suppressLoading) {
        setLoading(true);
      }

      try {
        const response = await fetcher(planId);
        if (!response?.success || !response?.data) {
          throw new Error(response?.message || 'Failed to load meal plan');
        }

        setData(response.data);
        setError(null);
        return response.data;
      } catch (err) {
        const normalizedError =
          err instanceof Error ? err : new Error('Failed to load meal plan');
        setError(normalizedError);
        return null;
      } finally {
        if (!suppressLoading) {
          setLoading(false);
        }
      }
    },
    [planId, fetcher]
  );

  useEffect(() => {
    if (planId) {
      refresh();
    } else {
      setData(null);
    }
  }, [planId, refresh]);

  return {
    data,
    setData,
    loading,
    error,
    refresh,
  };
};

export type UseMealPlanDataReturn = ReturnType<typeof useMealPlanData>;
